<template>
    <pc-input-group>
        <pc-label
            v-bind:label="label"
            v-bind:for-id="inputId"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            bold
            v-on:help-clicked="emitHelpClicked" />
        <div class="col-12">
            <slot name="help" />
        </div>
        <div v-if="element.GroupDataFields == null">
            {{ noTableSelected }}
        </div>
        <div v-else>
            <pc-loader v-bind:loading="updatingProjectTable">
                <pc-table
                    ref="pcProjectTable"
                    v-bind:items="tableItems"
                    v-bind:columns="tableColumns"
                    v-bind:empty-text="emptyTableText"
                    v-bind:hide-search="true"
                    v-bind:preserve-page="true"
                    display-table-when-empty
                    key-prop="index">
                    <template v-slot:index="row">
                        {{ row.index + 1 }}
                    </template>
                    <template
                        v-for="fieldId in visibleColumns"
                        v-slot:[fieldId]="row">
                        <table-item
                            v-if="editableColumns.includes(fieldId)"
                            v-bind:key="fieldId + 'item'"
                            style="min-width: 100px"
                            v-bind:datafield="fieldDefinitionFromId(fieldId)"
                            v-bind:is-required="requiredColumns.includes(fieldId)"
                            v-bind:readonly="readonly"
                            v-bind:model-value="row[fieldId]"
                            v-on:update:model-value="setTableValue(fieldId, row.index, $event)" />
                        <span
                            v-else
                            v-bind:key="fieldId">
                            {{ row[fieldId] }}
                        </span>
                    </template>
                    <template v-slot:actionsRow="row">
                        <pc-button-row>
                            <pc-button
                                v-if="canViewRow"
                                v-on:click="viewProject(row)">
                                {{ viewText }}
                            </pc-button>
                            <pc-button
                                v-if="canEditRow"
                                v-on:click="editProject(row)">
                                {{ editText }}
                            </pc-button>
                            <pc-button
                                v-if="canRemoveRow"
                                v-bind:disabled="readonly"
                                v-on:click="removeProjectRow(row)">
                                {{ removeLabel }}
                            </pc-button>
                        </pc-button-row>
                    </template>
                </pc-table>
                <pc-button-row>
                    <pc-button
                        v-if="canAddRow"
                        v-bind:disabled="readonly"
                        v-on:click="addProject">
                        {{ addLabel }}
                    </pc-button>
                    <pc-button
                        v-if="allowTableImport"
                        v-bind:disabled="readonly"
                        v-on:click="importNewRows">
                        {{ importNewProjectsButtonLabel }}
                    </pc-button>
                    <pc-button
                        v-if="allowAddExistingProject"
                        v-bind:disabled="readonly"
                        v-on:click="addExistingProject">
                        {{ addExistingProjectButtonLabel }}
                    </pc-button>
                </pc-button-row>
                <template v-slot:loadingMessage>
                    <project-table-progress-message />
                </template>
            </pc-loader>
        </div>
    </pc-input-group>
</template>

<script>
    import base from "./_elementBase";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/projectTable.i18n";
    import { KEYS as TABLE_KEYS, NAMESPACE as TABLE_NAMESPACE } from "./i18n/table.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { AUX_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
    import {
        PROJECT_FORM_CONTROLLER_TYPES,
        PROJECT_FORM_CONTROLLER_NAMESPACE
    } from "../ProjectForm/projectFormControllerStore";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { isModuleRegistered } from "SharedStores/util";
    import { mapGetters } from "vuex";
    import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
    import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";
    import tableItem from "./table/tableItem.vue";
    import importNewRowsDialog from "./table/importNewRowsDialog.vue";
    import addExistingProject from "./projectTable/addExistingProject.vue";
    import { DIALOG_HOOK, DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { useProjectLockStore } from "Projects/projectLockStore.js";
    import projectTableProgressMessage from "Forms/Components/Elements/projectTable/projectTableProgressMessage.vue";

    export const PROJECT_SUBMITTED = "projectSubmitted";
    export const PROJECT_TABLE_PARAM = "ProjectTableId";

    // Cap the number of project table items at 150; APS needs up to 100 for their program, and
    // 50% more should give them a bit of head room
    const MAX_PROJECT_TABLE_ITEMS = 150;

    export default {
        components: {
            tableItem,
            projectTableProgressMessage
        },
        extends: base,
        provide() {
            return { tableElement: this };
        },
        setup() {
            const projectLockStore = useProjectLockStore();

            return { projectLockStore };
        },
        data() {
            return {
                updatingProjectTable: false,
                addedEventListener: false
            };
        },
        computed: {
            tableItems() {
                if (!this.isProjectModuleRegistered()) {
                    return [];
                }
                const items = [];
                for (const fieldId of this.visibleColumns) {
                    const multiValues = this.multiValuesFromId(fieldId);
                    const dataField = this.dataFieldDefinitionFromId(fieldId);
                    for (let i = 0; i < multiValues.length; i++) {
                        items[i] = items[i] ?? { index: i };
                        let value = multiValues[i] ?? "";
                        if (dataField.ShowSeparators) {
                            value = THOUSAND_SEPARATOR_FORMATTER.format(value);
                        }
                        items[i][fieldId] = value;
                    }
                }
                for (const model of this.auxModel.ProjectRelationFormModels) {
                    const i = model.InstanceId;
                    items[i] = items[i] ?? { index: i };
                    items[i].status = model.CurrentStatus;
                }

                return items;
            },
            tableColumns() {
                const cols = [
                    {
                        header: this.rowHeader,
                        property: "index",
                        visible: true,
                        orderable: true
                    }
                ];
                for (const fieldId of this.orderedVisibleColumns) {
                    const suffix = this.element.AuxiliaryModel.fieldIdToSuffix[fieldId];
                    cols.push({
                        header: suffix,
                        property: fieldId,
                        visible: true,
                        // Don't let editable columns be sorted
                        orderable: !this.editableColumns.includes(fieldId),
                        showRequired: this.requiredColumns.includes(fieldId)
                    });
                }
                if (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.ShowRowStatus)) {
                    cols.push({
                        header: this.currentStatusHeader,
                        property: "status",
                        visible: true,
                        orderable: true
                    });
                }
                return cols;
            },
            requiredColumns() {
                const required = this.element.Properties[ELEMENT_PROPERTY_NAME.Required];
                return required ? required.split(",") : [];
            },
            visibleColumns() {
                const visibleColumns = this.element.Properties[ELEMENT_PROPERTY_NAME.VisibleColumns];
                return visibleColumns ? visibleColumns.split(",") : [];
            },
            editableColumns() {
                const editableColumns = this.element.Properties[ELEMENT_PROPERTY_NAME.EditableColumns];
                return editableColumns ? editableColumns.split(",") : [];
            },
            orderedVisibleColumns() {
                const columnOrderProperty = this.element.Properties[ELEMENT_PROPERTY_NAME.TableColumnOrder];
                let columnOrderArray = columnOrderProperty ? columnOrderProperty.split(",") : [];

                // Filter out any columns that are not visible
                columnOrderArray = columnOrderArray.filter((c) => this.visibleColumns.includes(c));

                // Add in any missing columns in DataFieldId order
                for (const fieldId of this.element.AuxiliaryModel.datafieldIds) {
                    if (this.visibleColumns.includes(fieldId) && !columnOrderArray.includes(fieldId)) {
                        columnOrderArray.push(fieldId);
                    }
                }
                return columnOrderArray;
            },
            canAddRow() {
                if (this.tableItems.length >= MAX_PROJECT_TABLE_ITEMS) {
                    return false;
                }
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.CanAddRow);
            },
            canEditRow() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.CanEditRow) && !this.readonly;
            },
            canViewRow() {
                return (
                    this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.CanViewRow) ||
                    (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.CanEditRow) && this.readonly)
                );
            },
            canRemoveRow() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.CanDeleteRow);
            },
            allowTableImport() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.AllowTableImport);
            },
            allowAddExistingProject() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.AllowProjectTableAddExistingProject);
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            auxModel() {
                if (!this.projectElement) {
                    return null;
                }
                return this.projectElement.auxState || this.projectElement.AuxiliaryModel;
            },
            projectLoadingUrl() {
                let url = this.auxModel.TargetProgramBaseUrl;
                url += this.$serviceControllers.ProjectTable.EditProjectLoadingUrl;
                return url.replace("(programId)", this.$app.ProgramId);
            },
            ...mapGetters({
                fieldDefinitionFromId: PROGRAM_TYPES.getters.dataFieldDefinitionFromId,
                multiValuesFromId: PROJECT_TYPES.getters.dataFieldMultiValueFromId,
                dataFieldDefinitionFromId: PROGRAM_TYPES.getters.dataFieldDefinitionFromId
            }),
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(TABLE_KEYS, TABLE_NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE)
        },
        mounted() {
            if (this.auxModel && this.auxModel.OperationId) {
                this.waitForImportOperation(this.auxModel.OperationId);
            }
        },
        methods: {
            async editProject(row) {
                if (!this.validateIsLinked() || !this.validateHasForm()) {
                    return;
                }

                // If this is a preview project (we don't have a project relation) just open the link
                const relationModel = this.auxModel.ProjectRelationFormModels[row.index];
                if (!relationModel.ProjectRelationId) {
                    const formUrl = this.getEditProjectUrl(relationModel);
                    if (formUrl) {
                        window.open(formUrl, "_blank");
                    }
                    return;
                }

                this.updatingProjectTable = true;
                try {
                    const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.editProjectTableRow,
                        { formToOpenSignalId: formToOpen, projectRelationId: relationModel.ProjectRelationId },
                        { root: true }
                    );
                    const formUrl = this.getEditProjectUrl(result.Data.ProjectRelationFormModel);
                    if (formUrl) {
                        this.openChildTab(formUrl);
                    }
                } finally {
                    this.updatingProjectTable = false;
                }
            },
            async viewProject(row) {
                if (!this.validateIsLinked() || !this.validateHasForm()) {
                    return;
                }

                // If this is a preview project (we don't have a project relation) just open the link
                const relationModel = this.auxModel.ProjectRelationFormModels[row.index];
                if (!relationModel.ProjectRelationId) {
                    const formUrl = this.getViewProjectUrl(relationModel);
                    if (formUrl) {
                        window.open(formUrl, "_blank");
                    }
                    return;
                }

                this.updatingProjectTable = true;
                try {
                    const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.viewProjectTableRow,
                        { formToOpenSignalId: formToOpen, projectRelationId: relationModel.ProjectRelationId },
                        { root: true }
                    );
                    const formUrl = this.getViewProjectUrl(result.Data.ProjectRelationFormModel);
                    window.open(formUrl, "_blank");
                } finally {
                    this.updatingProjectTable = false;
                }
            },
            async addProject() {
                if (!isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) return;

                if (!this.validateIsLinked() || !this.validateIsEnabled() || !this.validateHasForm()) {
                    return;
                }

                this.updatingProjectTable = true;
                try {
                    await this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.adminConfirmSaveFormData, null, {
                        root: true
                    });
                    const childTab = this.openChildTab(this.projectLoadingUrl);

                    const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.addProjectTableRow,
                        { formToOpenSignalId: formToOpen },
                        { root: true }
                    );

                    // Add the result to the aux model and data field list
                    const relationModel = result.Data.ProjectRelationFormModel;
                    /*
                     * Set the instanceId manually here if we are in a preview project
                     * The instanceId is used to match the project relation form model with the right
                     * row in the project table. Preview projects don't save project table data to the
                     * database and don't create a project relation, so adding a project table row
                     * doesn't return an instanceId
                     */
                    if (!relationModel.ProjectRelationId) {
                        relationModel.InstanceId = this.auxModel.ProjectRelationFormModels.length;
                    }
                    const auxModelCopy = { ...this.auxModel };
                    auxModelCopy.ProjectRelationFormModels = [...auxModelCopy.ProjectRelationFormModels, relationModel];
                    this.$store.commit(AUX_MUTATION, {
                        elementId: this.element.PublicId,
                        auxState: auxModelCopy
                    });
                    // Add an empty column to the table
                    for (const [, fieldId] of Object.entries(this.element.GroupDataFields)) {
                        const items = this.multiValuesFromId(fieldId);
                        // Add the new project relation if this is the project relation column
                        const newItems =
                            fieldId === this.auxModel.ProjectRelationFieldId
                                ? [...items, relationModel.ProjectRelationId]
                                : [...items, ""];
                        this.$store.commit(
                            PROJECT_TYPES.mutations.commitProjectMultiData,
                            { dataFieldId: fieldId, value: newItems },
                            { root: true }
                        );
                    }

                    // Open the new form
                    const formUrl = this.getEditProjectUrl(relationModel, true);
                    if (formUrl) {
                        this.redirectTab(childTab, formUrl);
                    } else {
                        childTab.close();
                    }
                } finally {
                    this.updatingProjectTable = false;
                }
            },
            async removeProjectRow(row) {
                if (!this.validateIsLinked() || !this.validateIsEnabled()) {
                    return;
                }
                const confirmDelete = await this.$app.showConfirmationDialog(null, this.removeRowConfirmationMessage);
                if (!confirmDelete) {
                    return;
                }

                this.updatingProjectTable = true;
                try {
                    const relationModel = this.auxModel.ProjectRelationFormModels[row.index];
                    // If we don't have a projectrelation (in a preview project) there is nothing to delete
                    if (relationModel.ProjectRelationId) {
                        await this.$store.dispatch(
                            PROJECT_FORM_CONTROLLER_TYPES.actions.removeProjectTableRow,
                            { projectRelationId: relationModel.ProjectRelationId },
                            { root: true }
                        );
                    }

                    // Remove the projectrelation from the auxmodel
                    const auxModelCopy = JSON.parse(JSON.stringify(this.auxModel));
                    auxModelCopy.ProjectRelationFormModels.splice(relationModel.InstanceId, 1);
                    for (let i = relationModel.InstanceId; i < auxModelCopy.ProjectRelationFormModels.length; i++) {
                        // Shift the instanceid of projectrelations after the one that was removed
                        auxModelCopy.ProjectRelationFormModels[i].InstanceId -= 1;
                    }
                    this.$store.commit(AUX_MUTATION, {
                        elementId: this.element.PublicId,
                        auxState: auxModelCopy
                    });

                    // Remove the project data
                    for (const [, fieldId] of Object.entries(this.element.GroupDataFields)) {
                        const items = this.multiValuesFromId(fieldId);
                        const itemsCopy = [...items];
                        itemsCopy.splice(relationModel.InstanceId, 1);
                        this.$store.commit(
                            PROJECT_TYPES.mutations.commitProjectMultiData,
                            { dataFieldId: fieldId, value: itemsCopy },
                            { root: true }
                        );
                    }
                } finally {
                    this.updatingProjectTable = false;
                }
            },
            async importNewRows() {
                if (!isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) return;

                if (!this.validateIsLinked() || !this.validateIsEnabled() || !this.validateHasForm()) {
                    return;
                }

                // Remove the status column if it is visible
                let tableColumns = this.tableColumns;
                if (this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.ShowRowStatus)) {
                    tableColumns = tableColumns.slice(0, -1);
                }

                const tableItemsOneIndexed = this.tableItems.map((item) => {
                    const oneIndexedItem = { ...item };
                    oneIndexedItem.index = oneIndexedItem.index + 1;
                    return oneIndexedItem;
                });

                const dialogResult = await this.$app.showDialog({
                    title: this.importNewRowsDialogHeader,
                    customComponent: importNewRowsDialog,
                    customComponentOptions: {
                        elementId: this.element.PublicId,
                        tableItems: tableItemsOneIndexed,
                        tableColumns: tableColumns,
                        maxTableLength: MAX_PROJECT_TABLE_ITEMS
                    },
                    extraLarge: true,
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    customComponentResultHook: "dialogResult",
                    buttons: [
                        {
                            text: this.cancelText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.okText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });

                if (dialogResult.action === DIALOG_RESULT.ok) {
                    const importResult = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.importProjectTableRows,
                        { formElementId: this.element.PublicId, documentId: dialogResult.data.DocumentId },
                        { root: true }
                    );
                    const operationId = importResult.Data.OperationId;

                    const auxModelCopy = { ...this.auxModel };
                    this.$store.commit(AUX_MUTATION, {
                        elementId: this.element.PublicId,
                        auxState: auxModelCopy
                    });

                    this.waitForImportOperation(operationId);
                }
            },
            async addExistingProject() {
                if (!isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) return;

                if (!this.validateIsLinked() || !this.validateIsEnabled() || !this.validateHasForm()) {
                    return;
                }

                const dialogResult = await this.$app.showDialog({
                    title: "Add Existing Project",
                    customComponent: addExistingProject,
                    customComponentOptions: {
                        findProjectAsync: this.findProjectAsync
                    },
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    customComponentResultHook: DIALOG_HOOK.getResult,
                    buttons: [
                        {
                            text: this.cancelText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.okText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });

                if (dialogResult.action !== DIALOG_RESULT.ok || !dialogResult.data) {
                    return;
                }

                this.updatingProjectTable = true;
                try {
                    const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                    const existingProjectId = dialogResult.data;
                    await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.addProjectTableExistingProject,
                        { formToOpenSignalId: formToOpen, existingProjectId: existingProjectId },
                        { root: true }
                    );
                } finally {
                    await this.waitForAutomations();
                    await this.refreshProjectTableData();
                }
            },
            async findProjectAsync(projectNumber) {
                const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.findProjectTableExistingProject,
                    {
                        formElementId: this.element.PublicId,
                        formToOpenSignalId: formToOpen,
                        projectNumber: projectNumber
                    },
                    { root: true }
                );
                return result.Data;
            },
            async waitForImportOperation(operationId) {
                if (!operationId) {
                    return;
                }
                this.updatingProjectTable = true;
                await this.projectLockStore.waitForProjectTableOperation(operationId);
                await this.refreshProjectTableData();
            },
            getEditProjectUrl(relationModel, isNew = false) {
                if (!this.validateIsLinked() || !this.validateHasForm()) {
                    return;
                }
                if (!relationModel.ProjectId) {
                    this.$app.showErrorDialog(null, this.projectNotFoundMessage);
                    return;
                }

                let url = this.auxModel.TargetProgramBaseUrl + this.$serviceControllers.ProjectTable.EditProjectUrl;
                if (isNew) {
                    url += this.auxModel.NewProjectUrlExt;
                }

                return url
                    .replace("(programId)", this.auxModel.TargetProgramId)
                    .replace("(formId)", this.auxModel.TargetFormId)
                    .replace("(projectId)", relationModel.ProjectId);
            },
            getViewProjectUrl(relationModel) {
                if (!this.validateIsLinked() || !this.validateHasForm()) {
                    return;
                }
                if (!relationModel.ProjectId) {
                    this.$app.showErrorDialog(null, this.projectNotFoundMessage);
                    return;
                }

                const url = this.auxModel.TargetProgramBaseUrl + this.$serviceControllers.ProjectTable.ViewProjectUrl;
                return url
                    .replace("(programId)", this.auxModel.TargetProgramId)
                    .replace("(formId)", this.auxModel.TargetFormId)
                    .replace("(projectId)", relationModel.ProjectId);
            },
            validateIsLinked() {
                if (!this.auxModel.IsLinked) {
                    this.$app.showErrorDialog(this.configurationErrorTitle, this.channelNotLinkedMessage);
                    return false;
                }
                return true;
            },
            validateIsEnabled() {
                if (!this.auxModel.IsEnabled) {
                    this.$app.showErrorDialog(this.configurationErrorTitle, this.channelNotEnabledMessage);
                    return false;
                }
                return true;
            },
            validateHasForm() {
                if (!this.auxModel.HasForm) {
                    this.$app.showErrorDialog(this.configurationErrorTitle, this.noFormConfiguredMessage);
                    return false;
                }
                return true;
            },
            openChildTab(url) {
                // Add listener here rather than on mount because we need to make sure the store is registered
                if (!this.addedEventListener) {
                    window.addEventListener("message", async (e) => {
                        // Make sure we get the right message from the right origin
                        if (
                            !this.auxModel ||
                            e.origin !== this.auxModel.TargetProgramBaseUrl ||
                            !e.data ||
                            e.data.message !== PROJECT_SUBMITTED
                        ) {
                            return;
                        }
                        // Make sure this is the right project table element
                        if (e.data[PROJECT_TABLE_PARAM] !== this.element.PublicId) {
                            return;
                        }

                        this.updatingProjectTable = true;
                        e.source.close();
                        await this.waitForAutomations();
                        await this.refreshProjectTableData();
                    });
                    this.addedEventListener = true;
                }
                // Add the ProjectTableId to the URL as a param
                return window.open(url + `&${PROJECT_TABLE_PARAM}=${this.element.PublicId}`, "_blank");
            },
            redirectTab(tab, url) {
                tab.location.href = url + `&${PROJECT_TABLE_PARAM}=${this.element.PublicId}`;
            },
            setTableValue(fieldId, index, value) {
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setProjectData,
                    { dataFieldId: fieldId, value, index },
                    { root: true }
                );
            },
            async waitForAutomations() {
                // Sleep for a second in case there is an asynchronous automation triggered
                // on submission. Project data may not get updated immediately if the update
                // signal is chained after a web adapter call
                await this.$app.timeout(1000);
            },
            async refreshProjectTableData() {
                if (!this.validateIsLinked()) {
                    return;
                }
                this.updatingProjectTable = true;
                try {
                    const formToOpen = this.element.Properties[ELEMENT_PROPERTY_NAME.FormToOpen];
                    const result = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.getProjectTableData,
                        { groupId: this.element.DataFieldGroupId, formToOpenSignalId: formToOpen },
                        { root: true }
                    );

                    for (const [fieldId, data] of Object.entries(result.Data.ProjectData)) {
                        this.$store.commit(
                            PROJECT_TYPES.mutations.commitProjectMultiData,
                            { dataFieldId: fieldId, value: data.MultiInstanceData },
                            { root: true }
                        );
                    }

                    const auxModelCopy = { ...this.auxModel };
                    auxModelCopy.ProjectRelationFormModels = result.Data.ProjectRelationFormModels;
                    this.$store.commit(AUX_MUTATION, {
                        elementId: this.element.PublicId,
                        auxState: auxModelCopy
                    });
                } finally {
                    this.updatingProjectTable = false;
                }
            }
        }
    };
</script>
